import { List, Record, RecordOf, fromJS } from 'immutable';

import {
  START_LOADING,
  CLEAR_NOTIFICATIONS,
  GET_DEALERSHIP_SETTINGS_SUCCESS,
  GET_DEALERSHIP_SETTINGS_FAIL,
  UPDATE_DEALERSHIP_SETTINGS_SUCCESS,
  UPDATE_DEALERSHIP_SETTINGS_FAIL,
  UPDATE_PAYMENT_ACCOUNT_LEGAL_ENTITY_SUCCESS,
  UPDATE_PAYMENT_ACCOUNT_LEGAL_ENTITY_FAIL,
  GET_PAYMENT_ACCOUNT_LEGAL_ENTITY_SUCCESS,
  GET_PAYMENT_ACCOUNT_LEGAL_ENTITY_FAIL,
  UPDATE_PAYMENT_ACCOUNT_LEGAL_ENTITY_DOCUMENT_SUCCESS,
  UPDATE_PAYMENT_ACCOUNT_LEGAL_ENTITY_DOCUMENT_FAIL,
  MAILCHIMP_INTEGRATION_SUCCESS,
  MAILCHIMP_INTEGRATION_FAIL,
  GET_MAILCHIMP_CUSTOMER_PROGRESS_SUCCESS,
  GET_MAILCHIMP_CUSTOMER_PROGRESS_FAIL,
  GET_MAILCHIMP_AUTH_URL_SUCCESS,
  GET_DEALERSHIP_DEPARTMENTS_SUCCESS,
  GET_DEALERSHIP_DEPARTMENTS_FAIL,
  UPDATE_DEALERSHIP_DEPARTMENT_FAIL,
  UPDATE_DEALERSHIP_DEPARTMENT_SUCCESS,
  CREATE_DEALERSHIP_DEPARTMENT_SUCCESS,
  CREATE_DEALERSHIP_DEPARTMENT_FAIL,
  DELETE_DEALERSHIP_DEPARTMENT_FAIL,
  DELETE_DEALERSHIP_DEPARTMENT_SUCCESS,
} from './actions';

import { GENERIC_ERROR } from 'constants/errors.constants';
import { DealershipDepartment, DealershipSettings } from 'services/resources';
import { UNITS } from 'constants/units';
import { FLEET_PLANS } from 'constants/plans';
import { Currency } from 'constants/common';

export const DealershipSettingsRecordFactory = Record<DealershipSettings>({
  address1: '',
  address2: '',
  city: '',
  country: '',
  currency: Currency.CAD,
  postalCode: '',
  state: '',
  betaFeaturesEnabled: false,
  brandingType: null,
  chargesFuelUnit: UNITS.liter.symbol,
  customerContactInterval: '14 00:00:00', // 14 days
  dealershipFuelUnit: UNITS.liter.symbol,
  dealershipOdometerUnit: UNITS.kilometer.symbol,
  dmsCustomerName: '',
  emailFontName: '',
  emailSubdomain: '',
  id: undefined,
  inboundPhoneNumber: '',
  inboundEmail: '',
  kimobyReadyToUse: false,
  latitude: null,
  longitude: null,
  notificationOverdueHoursLimit: 24,
  paymentAccountId: '',
  paymentsAcceptedTerms: false,
  paymentsAcceptedTermsIp: '',
  paymentsAcceptedTermsDate: '',
  paymentsAcceptedTermsUser: undefined,
  paymentsEnabled: false,
  planId: FLEET_PLANS.standard,
  replyForwardingDelay: '1 00:00:00', // 1 day
  smsPhoneNumber: '',
  streetAddress: '',
  timezone: '',
  website: '',
});

export const DealershipDepartmentRecordFactory = Record<DealershipDepartment>({
  id: undefined,
  name: '',
});

export interface DealershipSettingsState {
  errors: Record<any>;
  loading: boolean;
  mailChimpAuthUrl: string | null;
  mailChimpCustomerUploadProgress: Record<any>;
  mailChimpIntegrationSuccessful: boolean;
  notifications: List<any>;
  paymentAccountLegalEntity: Record<any>;
  settings: RecordOf<DealershipSettings>;
  departments: List<RecordOf<DealershipDepartment>>;
}

const DealershipSettingsStateRecordFactory = Record<DealershipSettingsState>({
  errors: Record<any>({})(),
  loading: false,
  mailChimpAuthUrl: null,
  mailChimpCustomerUploadProgress: Record<any>({})(),
  mailChimpIntegrationSuccessful: false,
  notifications: List(),
  paymentAccountLegalEntity: Record<any>({})(),
  settings: DealershipSettingsRecordFactory(),
  departments: List(),
});

const initialState = DealershipSettingsStateRecordFactory();

function dealershipSettingsReducer(state = initialState, action) {
  switch (action.type) {
    case START_LOADING:
      return state.set('loading', true);
    case GET_DEALERSHIP_SETTINGS_SUCCESS:
      return (
        state
          .set('settings', DealershipSettingsRecordFactory(action.payload.settings))
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS({}))
      );
    case GET_DEALERSHIP_SETTINGS_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GET_DEALERSHIP_DEPARTMENTS_SUCCESS: {
      return (
        state
          .set('departments', List(action.payload.departments.results.map(DealershipDepartmentRecordFactory)))
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS({}))
      );
    }
    case GET_DEALERSHIP_DEPARTMENTS_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case UPDATE_DEALERSHIP_DEPARTMENT_SUCCESS: {
      const updatedDepartment = action.payload.department;
      const departmentIndex = state.get('departments').findIndex((dept) => dept.get('id') === updatedDepartment.id);

      return (
        state
          .set(
            'departments',
            departmentIndex >= 0
              ? state.get('departments').set(departmentIndex, DealershipDepartmentRecordFactory(updatedDepartment))
              : state.get('departments')
          )
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS({}))
      );
    }
    case UPDATE_DEALERSHIP_DEPARTMENT_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case CREATE_DEALERSHIP_DEPARTMENT_SUCCESS: {
      const newDepartment = action.payload.department;
      return (
        state
          .set('departments', state.get('departments').push(DealershipDepartmentRecordFactory(newDepartment)))
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS({}))
      );
    }
    case CREATE_DEALERSHIP_DEPARTMENT_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case DELETE_DEALERSHIP_DEPARTMENT_SUCCESS: {
      const index = state.get('departments').findIndex((dept) => dept.id === action.payload.id);
      return (
        state
          .set('departments', state.get('departments').delete(index))
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS({}))
      );
    }
    case DELETE_DEALERSHIP_DEPARTMENT_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case UPDATE_DEALERSHIP_SETTINGS_SUCCESS:
      return (
        state
          .set('settings', DealershipSettingsRecordFactory(action.payload.settings))
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS({}))
      );
    case UPDATE_DEALERSHIP_SETTINGS_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GET_PAYMENT_ACCOUNT_LEGAL_ENTITY_SUCCESS:
      return (
        state
          .set('paymentAccountLegalEntity', fromJS(action.payload.paymentAccountLegalEntity))
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS({}))
      );
    case GET_PAYMENT_ACCOUNT_LEGAL_ENTITY_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case UPDATE_PAYMENT_ACCOUNT_LEGAL_ENTITY_SUCCESS:
      return (
        state
          .set('paymentAccountLegalEntity', fromJS(action.payload.paymentAccountLegalEntity))
          .set(
            'notifications',
            fromJS([
              {
                translationId: 'views.FleetSettings.PaymentDetails.update.success',
              },
            ])
          )
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS({}))
      );
    case UPDATE_PAYMENT_ACCOUNT_LEGAL_ENTITY_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case UPDATE_PAYMENT_ACCOUNT_LEGAL_ENTITY_DOCUMENT_SUCCESS:
      return (
        state
          .set('paymentAccountLegalEntity', fromJS(action.payload.paymentAccountLegalEntity))
          .set(
            'notifications',
            fromJS([
              {
                translationId: 'views.FleetSettings.PaymentDetails.documentUpdate.success',
              },
            ])
          )
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS({}))
      );
    case UPDATE_PAYMENT_ACCOUNT_LEGAL_ENTITY_DOCUMENT_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case MAILCHIMP_INTEGRATION_SUCCESS:
      // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
      return state.set('loading', false).set('mailChimpIntegrationSuccessful', true).set('errors', fromJS({}));
    case MAILCHIMP_INTEGRATION_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case GET_MAILCHIMP_CUSTOMER_PROGRESS_SUCCESS:
      return (
        state
          .set('mailChimpCustomerUploadProgress', fromJS(action.payload.mailChimpCustomerUploadProgress))
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS({}))
      );
    case GET_MAILCHIMP_AUTH_URL_SUCCESS:
      return state.set('mailChimpAuthUrl', fromJS(action.payload.mailChimpAuthUrl)).set('loading', false);
    case GET_MAILCHIMP_CUSTOMER_PROGRESS_FAIL:
      return state.set('loading', false).set('errors', fromJS(action.payload.errors || GENERIC_ERROR));
    case CLEAR_NOTIFICATIONS:
      // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
      return state.set('errors', fromJS({})).set('notifications', fromJS([]));
    default:
      return state;
  }
}

export default dealershipSettingsReducer;
